import React from 'react';
import { Spin } from 'antd';
import { CustomButton, CustomTitle } from '@/components';
import type { ShipmentProductList } from '@/apis/types';
import type { EditProductLine } from '../editProductLines';
import EditProductRows from './EditProductRows';
import type { PickerCatalogProps } from '../productPickerCatalog';
import ShipmentDetailProductsTable from './ShipmentDetailProductsTable';
import styles from './EditProductsSection.module.css';

/** Shown instead of the grid when the products came from an importer (lots or expiry). */
export const IMPORTER_PRODUCTS_NOTE =
  "Products on this shipment come from the importer and can't be edited here.";

/** Shown instead of the grid when the products are derived from the shipment's packages. */
export const PIECES_PRODUCTS_NOTE =
  "Products on this shipment come from its packages and can't be edited here.";

interface EditProductsSectionProps {
  rows: ShipmentProductList[];
  lines: EditProductLine[];
  onLinesChange: (lines: EditProductLine[]) => void;
  onProductIdsChange: (ids: string[]) => void;
  getProductName: (id: string) => string;
  catalog: PickerCatalogProps;
  readOnlyNote?: string;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
}

/** The edit drawer's Products: the product grid, or a read-only list with a note for importer and package shipments (spec UE2–UE4). */
const EditProductsSection: React.FC<EditProductsSectionProps> = ({
  rows,
  lines,
  onLinesChange,
  onProductIdsChange,
  getProductName,
  catalog,
  readOnlyNote,
  isLoading,
  isError,
  onRetry,
}) => {
  const body = (): React.ReactNode => {
    if (isLoading) {
      return (
        <div className={styles.status}>
          <Spin size='small' />
        </div>
      );
    }
    if (isError) {
      return (
        <div role='alert' className={styles.status}>
          <span>
            Couldn&apos;t load this shipment&apos;s products, so they can&apos;t
            be edited here.
          </span>
          <CustomButton
            text='Retry'
            height='28px'
            fontSize='12px'
            borderRadius='8px'
            bgColor='var(--light-gray)'
            color='var(--text-primary)'
            boxShadow='none'
            onClick={onRetry}
          />
        </div>
      );
    }
    if (readOnlyNote) {
      return (
        <>
          <span className={styles.note}>{readOnlyNote}</span>
          <ShipmentDetailProductsTable products={rows} previewCount={null} />
        </>
      );
    }
    return (
      <EditProductRows
        lines={lines}
        onLinesChange={onLinesChange}
        onProductIdsChange={onProductIdsChange}
        getProductName={getProductName}
        catalog={catalog}
      />
    );
  };

  return (
    <section className={styles.section} aria-label='Products'>
      <CustomTitle
        text='Products'
        fontSize='15px'
        fontWeight={700}
        lineHeight='28px'
        color='var(--text-primary)'
        marginBottom='0'
      />
      {body()}
    </section>
  );
};

export default EditProductsSection;
